import { useEffect, useRef, useState } from 'react';
import { request, ApiError } from '../lib/api.js';

/**
 * Signing in.
 *
 * Two steps, one screen: ask for the address, then for the six-digit code sent to it. There is
 * no password field anywhere, which is the point — nothing to remember, nothing to reuse from
 * another site's breach, nothing to reset from a rooftop in February.
 *
 * Three details that matter more than they look:
 *
 *   - **The address step never says whether the account exists.** It always advances to the
 *     code step. Telling a stranger "no such user" turns this form into a way to enumerate
 *     who works here.
 *   - **A link can carry both.** The operator command prints a URL with `?email=&code=`, and
 *     this submits it on arrival — so a sign-in link works as a link, without the recipient
 *     copying digits between windows.
 *   - **The code field is one input, not six boxes.** Six separate boxes look tidy and then
 *     fight paste, autofill, and every screen reader. One field with `autocomplete="one-time-code"`
 *     lets a phone offer the code from the notification.
 */

type Step = 'email' | 'code';

function readParams(): { email?: string; code?: string; next?: string } {
  if (typeof window === 'undefined') return {};
  const q = new URLSearchParams(window.location.search);
  return {
    email: q.get('email') ?? undefined,
    code: q.get('code') ?? undefined,
    // Only same-site paths: an open redirect here would let a crafted sign-in link bounce
    // somebody to another host immediately after authenticating.
    next: (() => {
      const n = q.get('next');
      return n && n.startsWith('/') && !n.startsWith('//') ? n : undefined;
    })(),
  };
}

export function SignInScreen() {
  /*
   * Read straight from the URL rather than through the router's typed search.
   *
   * This screen is reached by people who are not signed in, sometimes from a link in another
   * application entirely, and it ends with a full page load rather than a navigation. Coupling
   * it to the route tree buys nothing and makes it fail if the tree changes underneath it.
   */
  const search = readParams();

  const [step, setStep] = useState<Step>(search.code ? 'code' : 'email');
  const [email, setEmail] = useState(search.email ?? '');
  const [code, setCode] = useState(search.code ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const codeRef = useRef<HTMLInputElement>(null);
  /** Guards the auto-submit so React's development double-render cannot spend the code twice. */
  const autoSubmitted = useRef(false);

  async function requestCode(address: string) {
    setBusy(true);
    setError(null);
    try {
      await request('/auth/request-code', { method: 'POST', body: JSON.stringify({ email: address }) });
      setStep('code');
      setNotice(`Si un compte existe pour ${address}, un code à six chiffres vient d’être envoyé. Il est valide dix minutes.`);
      // Focus follows the step, or a keyboard user has to hunt for the new field.
      window.setTimeout(() => codeRef.current?.focus(), 0);
    } catch {
      // A failure here is the network or the server, never "no such account" — the endpoint
      // answers the same either way by design.
      setError('La connexion au serveur a échoué. Réessayez dans un instant.');
    } finally {
      setBusy(false);
    }
  }

  async function verify(address: string, value: string) {
    setBusy(true);
    setError(null);
    try {
      const result = await request<{ landing: 'portal' | 'field' }>('/auth/verify', {
        method: 'POST',
        body: JSON.stringify({ email: address, code: value }),
      });

      /*
       * A full page load, not a client-side navigation. The session cookie has just been set
       * and every cached query in memory belongs to nobody; reloading is the simplest way to
       * guarantee the app starts from the signed-in state rather than a half-updated one.
       */
      window.location.assign(result.landing === 'field' ? '/terrain' : (search.next ?? '/'));
    } catch (err) {
      const message =
        err instanceof ApiError && err.message
          ? err.message
          : 'Ce code ne correspond pas, ou il a expiré. Demandez-en un nouveau.';
      setError(message);
      setCode('');
      codeRef.current?.focus();
      setBusy(false);
    }
  }

  // A link carrying both parts signs the person in without them typing anything.
  useEffect(() => {
    if (autoSubmitted.current) return;
    if (search.email && search.code && /^\d{6}$/.test(search.code)) {
      autoSubmitted.current = true;
      void verify(search.email, search.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="signin">
      {/*
        The mark again, oversized and nearly invisible in the corner. Purely decorative — it
        carries no information the panel does not already give, so it is hidden from assistive
        technology and cannot be clicked through to.
      */}
      <svg className="signin__watermark" viewBox="0 0 64 48" fill="none" aria-hidden="true">
        <path d="M6 12 H28 C38 12 43 9 46 4" strokeWidth="7" strokeLinecap="round" />
        <path d="M6 26 H34 C44 26 49 23 52 18" strokeWidth="7" strokeLinecap="round" />
        <path d="M6 40 H40 C50 40 55 37 58 32" strokeWidth="7" strokeLinecap="round" />
      </svg>

      <main className="signin__panel">
        <div className="signin__brand">
          <svg width="34" height="26" viewBox="0 0 64 48" fill="none" aria-hidden="true">
            <path d="M6 12 H28 C38 12 43 9 46 4" stroke="#8e989f" strokeWidth="7" strokeLinecap="round" />
            <path d="M6 26 H34 C44 26 49 23 52 18" stroke="#2c343c" strokeWidth="7" strokeLinecap="round" />
            <path d="M6 40 H40 C50 40 55 37 58 32" stroke="#0e7fa8" strokeWidth="7" strokeLinecap="round" />
          </svg>
          <span className="signin__wordmark">
            monrdv<span className="signin__tld">.ca</span>
          </span>
        </div>

        {step === 'email' ? (
          <form
            className="signin__form"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim()) void requestCode(email.trim().toLowerCase());
            }}
          >
            <h1 className="signin__title">Connexion</h1>
            <p className="signin__lede">
              Entrez votre adresse courriel. Nous vous envoyons un code à usage unique — aucun mot
              de passe à retenir.
            </p>

            <label className="signin__label" htmlFor="email">
              Courriel
            </label>
            <input
              id="email"
              className="signin__input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@entreprise.ca"
              autoComplete="username"
              inputMode="email"
              required
              autoFocus
              disabled={busy}
            />

            <button className="signin__submit" type="submit" disabled={busy || !email.trim()}>
              {busy ? 'Un instant…' : 'Recevoir un code'}
            </button>
          </form>
        ) : (
          <form
            className="signin__form"
            onSubmit={(e) => {
              e.preventDefault();
              if (/^\d{6}$/.test(code)) void verify(email.trim().toLowerCase(), code);
            }}
          >
            <h1 className="signin__title">Entrez le code</h1>
            {notice && <p className="signin__lede">{notice}</p>}

            <label className="signin__label" htmlFor="code">
              Code à six chiffres
            </label>
            <input
              id="code"
              ref={codeRef}
              className="signin__input signin__input--code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              /*
               * `one-time-code` marks this as a code the platform may supply — and, just as
               * importantly, as a value browsers must not keep. A saved sign-in code is
               * useless to its owner (it is spent) and is a disclosure to anyone else at the
               * machine, so the field must never grow a dropdown of past attempts.
               *
               * The unnamed input reinforces it: form history is keyed on the field's name,
               * so a field without one has nothing to file previous entries under.
               */
              autoComplete="one-time-code"
              name=""
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              // `data-1p-ignore` / `data-lpignore` ask 1Password and LastPass to keep out;
              // both otherwise offer to fill a short numeric field.
              data-1p-ignore="true"
              data-lpignore="true"
              data-form-type="other"
              pattern="\d{6}"
              placeholder="••••••"
              required
              disabled={busy}
            />

            <button className="signin__submit" type="submit" disabled={busy || code.length !== 6}>
              {busy ? 'Vérification…' : 'Se connecter'}
            </button>

            <button
              className="signin__back"
              type="button"
              disabled={busy}
              onClick={() => {
                setStep('email');
                setCode('');
                setError(null);
                setNotice(null);
              }}
            >
              Utiliser une autre adresse
            </button>
          </form>
        )}

        {error && (
          <p className="signin__error" role="alert">
            {error}
          </p>
        )}
      </main>

      <p className="signin__foot">
        <a href="https://monrdv.ca/fr-ca/">monrdv.ca</a> · Mon Rendez-vous
      </p>
    </div>
  );
}
